// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ChevronLeft, ExternalLink, GitCommitHorizontal, Pencil, UserRound } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/components/app-shell/AppShell';
import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { GitHubMark } from '@/components/greenlight/GitHubMark';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { PendingCountBadge } from '@/components/greenlight/PendingCountBadge';
import { ResizableSlideOver } from '@/components/greenlight/ResizableSlideOver';
import { SegmentedTabs, type SegmentedTabsItem } from '@/components/greenlight/SegmentedTabs';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { RefreshWarning } from '@/components/greenlight/RefreshWarning';
import { pipelineMeta, statusMeta } from '@/components/greenlight/appStatusMeta';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { AD_META, AD_META_ITEM } from '@/components/greenlight/surfaces';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { isTransientQueryError, liveQueryOptions, statusPollInterval } from '@/lib/polling';
import { useCurrentUser } from '@/lib/auth';
import { cn } from '@/lib/utils';
import {
  AccessTab,
  AppLifecycleMenu,
  InactivityBanner,
  ChangeUrlDialog,
  ConfigurationTab,
  MonitoringTab,
  OverviewTab,
  PipelineRunDetail,
  PipelineTab,
  RenameAppDialog,
} from '@/components/app-detail';
import { resolveDeployUrl } from '@/components/app-detail/appDetailHelpers';
import { Tag } from '@/components/greenlight/Tag';

// Five tab groups over the docs/28 capability areas (grouping is a presentation
// choice; every area stays reachable): Overview carries about/imagery + the
// system-map graph, Access carries permissions + RBAC + co-owners, Monitoring
// carries resource metrics + logs, Configuration carries env vars. Lifecycle
// actions (pause/restore/delete) live in the header power menu, not a
// tab (AppLifecycleMenu).
type TabKey = 'overview' | 'pipeline' | 'access' | 'monitoring' | 'configuration';

interface TabDef {
  key: TabKey;
  label: string;
}

const ALL_TABS: readonly TabDef[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'pipeline', label: 'Deployments' },
  { key: 'access', label: 'Access' },
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'configuration', label: 'Configuration' },
];

// Pre-consolidation ?tab= values stay deep-linkable (docs/28 §Open UI questions
// Resolved): each legacy per-area value aliases into the group that owns the
// area now. The URL-rewrite effect below canonicalizes the address bar.
const LEGACY_TAB_ALIASES: Record<string, TabKey> = {
  permissions: 'access',
  rbac: 'access',
  'co-owners': 'access',
  logs: 'monitoring',
  resources: 'overview',
  env: 'configuration',
  settings: 'configuration',
};

export function AppDetailPage() {
  // `runId` is present when this page is rendered for /apps/:id/runs/:runId —
  // the run detail then opens as a slide-over layered on the page (docs/28
  // §Pipeline run detail: the route is normative, the panel is presentation).
  const { id = '', runId } = useParams<{ id: string; runId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  // All five groups render for every role; only the RBAC *section* inside
  // Access is IT-only (docs/28 §App detail Scoping).
  const showRbac = user?.role !== 'user';
  const [changeUrlOpen, setChangeUrlOpen] = React.useState(false);
  const [renameOpen, setRenameOpen] = React.useState(false);

  // The URL is the source of truth for the active tab — derived every render,
  // not seeded once into state. Legacy per-area values resolve through the
  // alias map; an open run slide-over defaults the backdrop to Pipeline.
  const tabParam = searchParams.get('tab');
  const requested = tabParam === null ? null : (LEGACY_TAB_ALIASES[tabParam] ?? tabParam);
  const activeTab: TabKey =
    ALL_TABS.find((t) => t.key === requested)?.key ?? (runId ? 'pipeline' : 'overview');

  const tabRefs = React.useRef<Map<TabKey, HTMLButtonElement | null>>(new Map());
  // Stable, memoized ref callback per tab key: a fresh inline arrow each render
  // would make React detach (call with null) and re-attach every cycle. The
  // handler deletes its entry on unmount so the map only holds mounted triggers.
  const tabRefHandlers = React.useRef<Map<TabKey, (el: HTMLButtonElement | null) => void>>(
    new Map(),
  );
  const tabRefFor = (key: TabKey): ((el: HTMLButtonElement | null) => void) => {
    let handler = tabRefHandlers.current.get(key);
    if (!handler) {
      handler = (el) => {
        if (el) tabRefs.current.set(key, el);
        else tabRefs.current.delete(key);
      };
      tabRefHandlers.current.set(key, handler);
    }
    return handler;
  };

  // Keep the address bar honest when the URL names a tab that isn't canonical:
  // a legacy alias (?tab=rbac → access) or an unknown value. Rewrite to the
  // resolved tab so the URL matches the visible pane. A bare URL with no ?tab=
  // is left untouched.
  React.useEffect(() => {
    if (tabParam !== null && tabParam !== activeTab) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', activeTab);
      setSearchParams(nextParams, { replace: true, preventScrollReset: true });
    }
  }, [tabParam, activeTab, searchParams, setSearchParams]);

  const { data, isLoading, isError, error } = api.apps.get.useQuery(
    ['apps.get', id],
    { params: { id } },
    {
      ...liveQueryOptions,
      queryKey: ['apps.get', id],
      refetchInterval: ({ state }) =>
        statusPollInterval([state.data?.body.app.status, state.data?.body.pipeline.latest_status]),
    },
  );

  // Same key as the Access tab's permissions list, so the badge and the list
  // share one cache entry. Drives the pending-grant count on the tab trigger.
  // Withheld (undefined) while loading or access is denied so the tab never shows a
  // misleading zero before data arrives.
  const permsQuery = api.permissions.list.useQuery(
    ['permissions', id],
    { params: { id } },
    { ...liveQueryOptions, queryKey: ['permissions', id] },
  );
  const pendingGrants =
    permsQuery.isLoading || (permsQuery.isError && !isTransientQueryError(permsQuery.error))
      ? undefined
      : permsQuery.data?.status === 200
        ? permsQuery.data.body.items.filter((p) => p.status === 'pending').length
        : undefined;

  const loaded = data?.status === 200 ? data.body : null;
  usePageTitle(loaded?.app.name ?? 'App detail');

  // Routed run-detail slide-over: /apps/:id/runs/:runId layers the run over
  // this page (docs/28 §Pipeline run detail). PipelineRunDetail loads from its
  // own getRun query, so the panel renders even while the backdrop app fetch is
  // still loading or has failed. Closing replaces the history entry so Back
  // doesn't land on the run URL and reopen the panel.
  const runSlideOver = runId !== undefined && (
    <ResizableSlideOver
      open
      onOpenChange={(open) => {
        if (!open) navigate(`/apps/${id}?tab=pipeline`, { replace: true });
      }}
      title="Pipeline run"
      description="Every check for this run — pipeline diagnostics, not runtime logs."
      defaultWidth={560}
      storageKey="gl-run-panel-width"
    >
      <PipelineRunDetail appId={id} runId={runId} />
    </ResizableSlideOver>
  );

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="app-detail-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        {runSlideOver}
      </div>
    );
  }

  if ((isError && !isTransientQueryError(error)) || !data || data.status !== 200) {
    return (
      <div>
        <Alert variant="destructive" data-testid="app-detail-error">
          <AlertTitle>Couldn't load app</AlertTitle>
          <AlertDescription>
            The app may have been deleted. <Link to="/apps">Back to Apps</Link>
          </AlertDescription>
        </Alert>
        {runSlideOver}
      </div>
    );
  }

  const detail = data.body;
  const { app } = detail;
  const status = statusMeta(app.status);
  const pipelineStatus = pipelineMeta(detail.pipeline.latest_status ?? '');
  const deployUrl = resolveDeployUrl(detail);
  // Build the tab items for the segmented control, injecting a pending-grants
  // badge on the Access tab when there are unreviewed grants.
  const tabItems: SegmentedTabsItem[] = ALL_TABS.map((t) => ({
    value: t.key,
    testId: `tab-${t.key}`,
    id: `app-tab-${t.key}`,
    controls: `app-panel-${t.key}`,
    refCallback: tabRefFor(t.key),
    label:
      t.key === 'access' && pendingGrants !== undefined && pendingGrants > 0 ? (
        <>
          {t.label}
          <PendingCountBadge
            count={pendingGrants}
            label={`${pendingGrants} pending grants`}
            className="ml-1.5"
          />
        </>
      ) : (
        t.label
      ),
  }));

  // Commit SHA from manifest_sha (the latest deployed commit).
  const commitSha = detail.manifest_sha ?? null;

  return (
    <div className="space-y-6" data-testid="app-detail-page">
      {isError && <RefreshWarning />}
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div>
        <Link
          to="/apps"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" aria-hidden /> All Apps
        </Link>

        {/* Three-zone row: icon · title block · power button */}
        <div className="mt-4 flex items-start gap-4">
          {/* 92px squircle icon */}
          <div className="shrink-0">
            <AppIconTile
              iconUrl={app.icon_url}
              name={app.name}
              slug={app.slug}
              size={92}
              radius={22}
            />
          </div>

          {/* Title block — icon + name/status row own the top zone; the
              metadata strip lives on its own full-width row below (§2). */}
          <div className="min-w-0 flex-1">
            {/* Eyebrow */}
            <p className="font-mono text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
              App
            </p>
            <div className="mt-0.5 flex items-center gap-1.5">
              <h1 className="text-2xl md:text-[28px] font-semibold tracking-[-0.02em]">
                {app.name}
              </h1>
              {/* Display-name rename; slug and URL are separate identities
                  (ADR-0050). Server enforces who may PATCH, as elsewhere. */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground"
                      aria-label="Rename app"
                      data-testid="rename-app-btn"
                      onClick={() => setRenameOpen(true)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Rename</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <StatusBadge variant={status.variant}>{status.label}</StatusBadge>
              {pipelineStatus.variant === 'deploying' && status.variant !== 'deploying' && (
                <StatusBadge variant="deploying">
                  {pipelineStatus.label === 'Running' ? 'Pipeline running' : pipelineStatus.label}
                </StatusBadge>
              )}
              {/* The public address (serving_subdomain ?? slug, ADR-0050) — what
                  users visit and what the /apps chip shows, not the internal slug. */}
              <MonoValue>{app.serving_subdomain ?? app.slug}</MonoValue>
            </div>
            {/* Category tags declared in greenlight.yml (ADR-0032) — read-only
                here; the /apps list filters by them. */}
            {app.tags && app.tags.length > 0 && (
              <ul
                className="mt-2 flex flex-wrap items-center gap-1.5"
                aria-label="Tags"
                data-testid="app-tags"
              >
                {app.tags.map((tag) => (
                  <li key={tag}>
                    <Tag>{tag}</Tag>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Power button — soft destructive tint, alone on the far right */}
          <AppLifecycleMenu appId={id} appName={app.name} slug={app.slug} status={app.status} />
        </div>

        {/* The inactivity warning, directly under the title block: this page is
            where the owner's email sends them, and the banner is the reason. */}
        <InactivityBanner
          appId={id}
          appName={app.name}
          inactivity={detail.inactivity}
          deployUrl={deployUrl}
        />

        {/* Metadata strip — its own full-width row beneath the title block.
            Borderless inline cells separated by vertical hairlines, wrapping
            gracefully; dividers are suppressed on wrapped cells (§2). */}
        {(deployUrl || app.repo_full_name || commitSha || app.owner_email) && (
          <div className={cn(AD_META, 'mt-4')}>
            {deployUrl && (
              <span className="flex min-w-0 items-center gap-0.5">
                <a
                  href={deployUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={cn(AD_META_ITEM, 'text-link hover:text-link/80')}
                >
                  <ExternalLink className="size-3.5 shrink-0" aria-hidden />
                  <span className="truncate max-w-[220px]">{deployUrl}</span>
                </a>
                {/* URL is editable for a deployed app (ADR-0050); server enforces
                    who may change it, same as the rest of the PATCH surface. */}
                {detail.shared_environment.domain && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          aria-label="Change app URL"
                          data-testid="change-url-btn"
                          onClick={() => setChangeUrlOpen(true)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Change URL</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </span>
            )}
            {app.repo_full_name && (
              <a
                href={`https://github.com/${app.repo_full_name}`}
                target="_blank"
                rel="noreferrer"
                className={AD_META_ITEM}
              >
                <GitHubMark className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate max-w-[200px]">{app.repo_full_name}</span>
              </a>
            )}
            {commitSha && (
              <span className={AD_META_ITEM}>
                <GitCommitHorizontal className="size-3.5 shrink-0" aria-hidden />
                <MonoValue>{commitSha.slice(0, 7)}</MonoValue>
              </span>
            )}
            {app.owner_email && (
              <a
                href={`mailto:${app.owner_email}`}
                aria-label={`Owner ${app.owner_name ?? app.owner_email}`}
                className={AD_META_ITEM}
              >
                <UserRound className="size-3.5 shrink-0" aria-hidden />
                {/* Name leads; email follows in muted text (falls back to
                    email-only when no display name is set). */}
                {app.owner_name ? (
                  <span className="flex min-w-0 items-baseline gap-1.5">
                    <span className="truncate">{app.owner_name}</span>
                    <span className="truncate text-xs font-normal text-muted-foreground">
                      {app.owner_email}
                    </span>
                  </span>
                ) : (
                  <span className="truncate max-w-[200px]">{app.owner_email}</span>
                )}
              </a>
            )}
          </div>
        )}

        {detail.shared_environment.domain && (
          <ChangeUrlDialog
            appId={id}
            appName={app.name}
            slug={app.slug}
            servingSubdomain={app.serving_subdomain ?? null}
            domain={detail.shared_environment.domain}
            open={changeUrlOpen}
            onOpenChange={setChangeUrlOpen}
          />
        )}
        <RenameAppDialog
          appId={id}
          name={app.name}
          open={renameOpen}
          onOpenChange={setRenameOpen}
        />

        {/* A queued deletion is a promise the teardown worker keeps; when it
            reports failing steps, say so instead of a bare badge — recovery is
            the power menu's Retry deletion (docs/08 §Delete flow). */}
        {app.status === 'queued_for_deletion' && (
          <Alert
            variant={app.deletion_state?.failing_steps?.length ? 'destructive' : 'default'}
            className="mt-4"
            data-testid="deletion-state-banner"
          >
            <AlertTitle>
              {app.deletion_state?.failing_steps?.length
                ? 'Deletion is failing'
                : 'Deletion in progress'}
            </AlertTitle>
            <AlertDescription>
              {app.deletion_state?.queued_at && (
                <>Queued {relativeTime(app.deletion_state.queued_at)}. </>
              )}
              {app.deletion_state?.failing_steps?.length ? (
                <>
                  Teardown is stuck at:{' '}
                  <span className="font-mono">{app.deletion_state.failing_steps.join(', ')}</span>.
                  Use the power menu&apos;s &quot;Retry deletion&quot; to resume, and check the
                  audit log for the step&apos;s error.
                </>
              ) : (
                <>Remaining infrastructure is being torn down; this page will update.</>
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.set('tab', v);
          setSearchParams(nextParams, { replace: true, preventScrollReset: true });
        }}
      >
        {/* Sticky segmented control — blurred background keeps it readable when
            the page scrolls behind it. */}
        <div className="sticky top-0 z-10 -mx-4 px-4 pb-2 pt-1 bg-background/85 backdrop-blur-sm sm:-mx-6 sm:px-6">
          <SegmentedTabs
            items={tabItems}
            value={activeTab}
            onValueChange={(v) => {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.set('tab', v);
              setSearchParams(nextParams, { replace: true, preventScrollReset: true });
            }}
            aria-label="App sections"
            className="w-full sm:w-auto"
          />
        </div>

        <TabsContent value="overview" id="app-panel-overview" aria-labelledby="app-tab-overview">
          {/* OverviewTab owns both rows: hero+about (row 1) and
              system-map+recent-deployments (row 2) — all data flows in as
              props so the tab keeps one testid root. */}
          <OverviewTab
            detail={detail}
            appId={id}
            screenshotUrls={app.screenshot_urls}
            appUrl={deployUrl}
            docSummary={app.doc_summary ?? null}
            docPurpose={app.doc_purpose ?? null}
            docArchitecture={app.doc_architecture ?? null}
            description={app.description ?? null}
          />
        </TabsContent>
        <TabsContent value="pipeline" id="app-panel-pipeline" aria-labelledby="app-tab-pipeline">
          <PipelineTab appId={id} />
        </TabsContent>
        <TabsContent value="access" id="app-panel-access" aria-labelledby="app-tab-access">
          <AccessTab
            appId={id}
            showRbac={showRbac}
            ownerEmail={app.owner_email ?? null}
            ownerName={app.owner_name ?? null}
            accessRestricted={app.access_restricted}
          />
        </TabsContent>
        <TabsContent
          value="monitoring"
          id="app-panel-monitoring"
          aria-labelledby="app-tab-monitoring"
        >
          <MonitoringTab appId={id} />
        </TabsContent>
        <TabsContent
          value="configuration"
          id="app-panel-configuration"
          aria-labelledby="app-tab-configuration"
        >
          <ConfigurationTab appId={id} />
        </TabsContent>
      </Tabs>

      {runSlideOver}
    </div>
  );
}
